import React, { useEffect, useState } from "react";
import InputMask from "react-input-mask";
import { Search, Save } from "lucide-react";
import { listarDistribuidoresDropdown, listarTabelasPreco } from "../services/api";

const CadastroRevenda = ({ token, initialData = {}, isEdit = false, onSubmit }) => {
  const [revenda, setRevenda] = useState({
    ativo: true,
    cnpj: "",
    inscricao_estadual: "",
    razao_social: "",
    nome_fantasia: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cep: "",
    cidade: "",
    estado: "",
    email: "",
    telefone: "",
    simples_nacional: true,
    tabela_preco_id: "",
    distribuidor_id: "",
    exibir_precos: false,
    produto_zen_pdv: true,
    produto_zen_tag: true,
    produto_zen_gestao: true,
    produto_zen_mobile: true,
    produto_zen_pass: true,
    ...initialData,
  });

  const [loadingCnpj, setLoadingCnpj] = useState(false);
  const [tabelasPreco, setTabelasPreco] = useState([]);
  const [distribuidores, setDistribuidores] = useState([]);

  useEffect(() => {
    if (!token) return;
    listarTabelasPreco(token, { ativo: true })
      .then((resp) => setTabelasPreco(Array.isArray(resp?.data) ? resp.data : []))
      .catch(() => setTabelasPreco([]));
    listarDistribuidoresDropdown(token)
      .then((resp) => setDistribuidores(Array.isArray(resp?.data) ? resp.data : []))
      .catch(() => setDistribuidores([]));
  }, [token]);

  useEffect(() => {
    if (isEdit && initialData && Object.keys(initialData).length > 0) {
      setRevenda({
        ativo: typeof initialData.ativo === "boolean" ? initialData.ativo : !!initialData.ativo,
        cnpj: initialData.cnpj || "",
        inscricao_estadual: initialData.inscricaoEstadual || "",
        razao_social: initialData.razaoSocial || "",
        nome_fantasia: initialData.nomeFantasia || "",
        logradouro: initialData.logradouro || "",
        numero: initialData.numero || "",
        complemento: initialData.complemento || "",
        bairro: initialData.bairro || "",
        cep: initialData.cep || "",
        cidade: initialData.cidade || "",
        estado: initialData.estado || "",
        email: initialData.email || "",
        telefone: initialData.telefone || "",
        simples_nacional: initialData.simplesNacional ?? true,
        tabela_preco_id: initialData.tabelaPrecoId ? String(initialData.tabelaPrecoId) : "",
        distribuidor_id: initialData.distribuidorId ? String(initialData.distribuidorId) : "",
        exibir_precos: !!initialData.exibirPrecos,
        produto_zen_pdv: initialData.produtoZenPdv !== false,
        produto_zen_tag: initialData.produtoZenTag !== false,
        produto_zen_gestao: initialData.produtoZenGestao !== false,
        produto_zen_mobile: initialData.produtoZenMobile !== false,
        produto_zen_pass: initialData.produtoZenPass !== false,
      });
    }
  }, [initialData, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setRevenda((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const soDigitos = (v) => String(v || "").replace(/\D/g, "");

    // camelCase alinhado ao schema Revenda da ZenAPI (additionalProperties: false).
    const payload = {
      ativo: Boolean(revenda.ativo),
      razaoSocial: (revenda.razao_social || "").trim(),
      nomeFantasia: (revenda.nome_fantasia || "").trim(),
      cnpj: soDigitos(revenda.cnpj),
      logradouro: (revenda.logradouro || "").trim(),
      numero: (revenda.numero || "").trim(),
      bairro: (revenda.bairro || "").trim(),
      cidade: (revenda.cidade || "").trim(),
      estado: (revenda.estado || "").trim().toUpperCase(),
      cep: soDigitos(revenda.cep),
      email: (revenda.email || "").trim(),
      telefone: soDigitos(revenda.telefone),
      inscricaoEstadual: (revenda.inscricao_estadual || "").trim(),
      simplesNacional: !!revenda.simples_nacional,
      complemento: (revenda.complemento || "").trim(),
      tabelaPrecoId: revenda.tabela_preco_id ? Number(revenda.tabela_preco_id) : null,
      distribuidorId: revenda.distribuidor_id ? Number(revenda.distribuidor_id) : null,
      exibirPrecos: !!revenda.exibir_precos,
      produtoZenPdv: !!revenda.produto_zen_pdv,
      produtoZenTag: !!revenda.produto_zen_tag,
      produtoZenGestao: !!revenda.produto_zen_gestao,
      produtoZenMobile: !!revenda.produto_zen_mobile,
      produtoZenPass: !!revenda.produto_zen_pass,
    };

    if (payload.cnpj.length !== 14) {
      alert("Informe um CNPJ válido com 14 dígitos.");
      return;
    }

    try {
      await onSubmit(payload, token);
    } catch (err) {
      const data = err?.response?.data;

      if (typeof data === "string") {
        alert(data);
        return;
      }

      const message =
        data?.message ||
        data?.title ||
        (data?.errors && Object.values(data.errors).flat().join("\n")) ||
        err.message ||
        "Erro ao salvar revenda.";

      alert(message);
      console.error(err);
    }
  };

  const handleBuscarCNPJ = async () => {
    const cnpj = (revenda.cnpj || "").replace(/\D/g, "");
    if (!cnpj || cnpj.length !== 14) {
      alert("Informe um CNPJ válido.");
      return;
    }

    setLoadingCnpj(true);
    try {
      const res = await fetch(`https://publica.cnpj.ws/cnpj/${cnpj}`);
      const data = await res.json();

      const est = data?.estabelecimento;

      setRevenda((prev) => ({
        ...prev,
        razao_social: data?.razao_social || prev.razao_social || "",
        nome_fantasia: est?.nome_fantasia || prev.nome_fantasia || "",
        logradouro: est?.logradouro || prev.logradouro || "",
        numero: est?.numero || prev.numero || "",
        complemento: est?.complemento || prev.complemento || "",
        bairro: est?.bairro || prev.bairro || "",
        cep: est?.cep || prev.cep || "",
        cidade: est?.cidade?.nome || prev.cidade || "",
        estado: est?.estado?.sigla || prev.estado || "",
        email: est?.email || prev.email || "",
        telefone: est?.telefone1 || prev.telefone || "",
        inscricao_estadual:
          est?.inscricoes_estaduais?.[0]?.inscricao_estadual || prev.inscricao_estadual || "",
        simples_nacional: data?.simples?.simples === "Sim",
      }));
    } catch (err) {
      console.error("Erro ao buscar CNPJ:", err);
      alert("Erro ao buscar CNPJ. Tente novamente.");
    } finally {
      setLoadingCnpj(false);
    }
  };

  // classes padrão (mesmo estilo ZenTag)
  const inputClass = `
    w-full px-3 py-2.5 rounded-xl border outline-none transition
    zen-card border-gray-300 text-gray-900 placeholder:text-gray-400
    focus:ring-2 focus:ring-blue-500/60
    dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
    dark:focus:ring-blue-400/40
  `;

  const labelClass = "block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1";

  return (
    <div className="max-w-5xl mx-auto">
      <div className="zen-card rounded-2xl p-6 md:p-8 shadow-sm dark:backdrop-blur">
        {/* header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              {isEdit ? "Editar Revenda" : "Cadastro de Revenda"}
            </h2>
            <p className="text-sm text-gray-500 dark:text-slate-300/80 mt-1">
              Preencha os dados para {isEdit ? "editar a" : "cadastrar uma nova"} revenda.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* bloco 1 */}
          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <label className={labelClass}>CNPJ</label>
                <div className="flex">
                  <InputMask
                    mask="99.999.999/9999-99"
                    name="cnpj"
                    value={revenda.cnpj}
                    onChange={handleChange}
                    className={`${inputClass} rounded-r-none`}
                    required
                  />
                  <button
                    onClick={handleBuscarCNPJ}
                    type="button"
                    disabled={loadingCnpj}
                    className="
                      px-4 py-2.5 rounded-r-xl font-semibold transition
                      bg-blue-600 text-white hover:bg-blue-500
                      disabled:opacity-60 disabled:cursor-not-allowed
                      inline-flex items-center justify-center
                    "
                    title="Buscar dados pelo CNPJ"
                  >
                    <Search size={18} />
                  </button>
                </div>
                {loadingCnpj && (
                  <p className="text-xs mt-1 text-gray-500 dark:text-slate-300/70">
                    Buscando dados do CNPJ...
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Inscrição Estadual</label>
                <input
                  name="inscricao_estadual"
                  value={revenda.inscricao_estadual}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              <div className="md:col-span-3">
                <label className={labelClass}>Razão Social</label>
                <input
                  name="razao_social"
                  value={revenda.razao_social}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div className="md:col-span-3">
                <label className={labelClass}>Nome Fantasia</label>
                <input
                  name="nome_fantasia"
                  value={revenda.nome_fantasia}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>
            </div>
          </div>

          {/* bloco 2 */}
          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-3">
                <label className={labelClass}>Logradouro</label>
                <input
                  name="logradouro"
                  value={revenda.logradouro}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div className="md:col-span-1">
                <label className={labelClass}>Número</label>
                <input
                  name="numero"
                  value={revenda.numero}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Complemento</label>
                <input
                  name="complemento"
                  value={revenda.complemento}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Bairro</label>
                <input
                  name="bairro"
                  value={revenda.bairro}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div className="md:col-span-1">
                <label className={labelClass}>CEP</label>
                <InputMask
                  mask="99999-999"
                  name="cep"
                  value={revenda.cep}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Cidade</label>
                <input
                  name="cidade"
                  value={revenda.cidade}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>

              <div className="md:col-span-1">
                <label className={labelClass}>Estado</label>
                <input
                  name="estado"
                  value={revenda.estado}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>
            </div>
          </div>

          {/* bloco 3 */}
          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={revenda.email}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Telefone</label>
                <InputMask
                  mask="(99) 99999-9999"
                  name="telefone"
                  value={revenda.telefone}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              <div className="md:col-span-2 border-t border-gray-100 dark:border-white/10 pt-4 mt-2">
                <p className="text-sm font-semibold text-gray-800 dark:text-slate-100 mb-3">Licenciamento</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Distribuidor</label>
                    <select
                      name="distribuidor_id"
                      value={revenda.distribuidor_id || ""}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="">— Direto (sem distribuidor) —</option>
                      {distribuidores.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.nomeFantasia || d.razaoSocial}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Tabela de preço</label>
                    <select
                      name="tabela_preco_id"
                      value={revenda.tabela_preco_id}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="">— Nenhuma —</option>
                      {tabelasPreco
                        .filter((t) =>
                          revenda.distribuidor_id
                            ? String(t.distribuidorId || "") === String(revenda.distribuidor_id)
                            : !t.distribuidorId
                        )
                        .map((t) => (
                          <option key={t.id} value={t.id}>{t.nome}</option>
                        ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Com distribuidor, use a tabela que ele criou para cobrar esta revenda.
                    </p>
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        name="exibir_precos"
                        checked={!!revenda.exibir_precos}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-white/20 dark:bg-slate-950/40"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-slate-200">
                        Revenda pode ver preços no portal
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-800 dark:text-slate-100 mb-2">
                    Produtos ativos
                  </p>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
                    Controla quais produtos a revenda pode comercializar no portal.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { name: "produto_zen_pdv", label: "ZenPDV Desktop" },
                      { name: "produto_zen_tag", label: "ZenTag" },
                      { name: "produto_zen_gestao", label: "ZenGestão" },
                      { name: "produto_zen_mobile", label: "ZenMobile" },
                      { name: "produto_zen_pass", label: "ZenPass" },
                    ].map((p) => (
                      <label
                        key={p.name}
                        className="inline-flex items-center gap-2 cursor-pointer select-none rounded-xl border border-gray-200 dark:border-white/10 px-3 py-2"
                      >
                        <input
                          type="checkbox"
                          name={p.name}
                          checked={!!revenda[p.name]}
                          onChange={handleChange}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-white/20 dark:bg-slate-950/40"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-slate-200">
                          {p.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="md:col-span-2 flex flex-wrap items-center gap-6 pt-1">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="simples_nacional"
                    checked={!!revenda.simples_nacional}
                    onChange={handleChange}
                    className="
                      h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500
                      dark:border-white/20 dark:bg-slate-950/40
                    "
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-slate-200">
                    Simples Nacional
                  </span>
                </label>

                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="ativo"
                    checked={!!revenda.ativo}
                    onChange={(e) =>
                      setRevenda((prev) => ({ ...prev, ativo: e.target.checked }))
                    }
                    className="
                      h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500
                      dark:border-white/20 dark:bg-slate-950/40
                    "
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-slate-200">
                    Ativa
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* actions */}
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              className="
                inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition
                bg-blue-600 text-white hover:bg-blue-500
              "
            >
              <Save size={18} />
              {isEdit ? "Salvar Alterações" : "Cadastrar Revenda"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CadastroRevenda;
